import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  X,
  ArrowUp,
  ArrowDown,
  Save,
  Upload,
  Loader2,
  Image as ImageIcon,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BLOG_CATEGORIES,
  fetchAdminBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost
} from '../blog/blogData';

const EMPTY_FORM = {
  slug: '',
  title: '',
  excerpt: '',
  category: 'moving-tips',
  author: 'Swift Move and Clean Team',
  date: new Date().toISOString().split('T')[0],
  readTime: '5 min read',
  cover: '',
  tags: '',
  keywords: '',
  content: [{ type: 'paragraph', text: '' }],
  published: true
};

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

// Resolve a stored upload path (e.g. "/api/uploads/images/xxx.jpg") to a full URL
// for preview inside the admin editor. External URLs are returned as-is.
const resolveImageSrc = (src) => {
  if (!src) return '';
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  }
  return src;
};

// Upload a single image file to the backend and return the stored path.
const uploadImageFile = async (file) => {
  if (!file.type?.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('Image must be under 10MB');
  }
  const fd = new FormData();
  fd.append('files', file);
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/uploads/images`,
    { method: 'POST', body: fd }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  const json = await res.json();
  return json.files?.[0]?.path || '';
};

export const BlogTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverDragging, setCoverDragging] = useState(false);
  const [blockUploading, setBlockUploading] = useState({}); // { [idx]: true }
  const excerptRef = useRef(null);
  const headingRefs = useRef({});
  const paragraphRefs = useRef({});

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminBlogPosts(token);
      setPosts(data);
    } catch (e) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowEditor(true);
  };

  const openEdit = (post) => {
    setEditingId(post.id);
    setForm({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt,
      category: post.category,
      author: post.author,
      date: post.date,
      readTime: post.readTime,
      cover: post.cover,
      tags: (post.tags || []).join(', '),
      keywords: post.keywords || '',
      content: post.content && post.content.length > 0
        ? post.content
        : [{ type: 'paragraph', text: '' }],
      published: post.published !== false
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  // Content block operations
  const addBlock = (type) => {
    let newBlock;
    if (type === 'list') {
      newBlock = { type, items: [''] };
    } else if (type === 'image') {
      newBlock = { type, text: '', caption: '' };
    } else {
      newBlock = { type, text: '' };
    }
    setForm((f) => ({ ...f, content: [...f.content, newBlock] }));
  };

  const updateBlock = (idx, patch) => {
    setForm((f) => ({
      ...f,
      content: f.content.map((b, i) => (i === idx ? { ...b, ...patch } : b))
    }));
  };

  const buildMarkdownLink = ({ input, currentText }) => {
    const selectionStart = input?.selectionStart ?? currentText.length;
    const selectionEnd = input?.selectionEnd ?? currentText.length;
    const selectedText = currentText.slice(selectionStart, selectionEnd);
    const linkText = selectedText || window.prompt('Link text');
    if (!linkText) return null;

    const href = window.prompt('Link URL', '/#quote');
    if (!href) return null;

    const markdownLink = `[${linkText}](${href.trim()})`;
    const nextText = `${currentText.slice(0, selectionStart)}${markdownLink}${currentText.slice(selectionEnd)}`;
    return { nextText, cursorPosition: selectionStart + markdownLink.length };
  };

  const focusAfterLinkInsert = (input, cursorPosition) => {
    window.requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const insertLinkIntoExcerpt = () => {
    const input = excerptRef.current;
    const result = buildMarkdownLink({ input, currentText: form.excerpt || '' });
    if (!result) return;

    setForm((f) => ({ ...f, excerpt: result.nextText }));
    focusAfterLinkInsert(input, result.cursorPosition);
  };

  const insertLinkIntoHeading = (idx) => {
    const input = headingRefs.current[idx];
    const currentText = form.content[idx]?.text || '';
    const result = buildMarkdownLink({ input, currentText });
    if (!result) return;

    updateBlock(idx, { text: result.nextText });
    focusAfterLinkInsert(input, result.cursorPosition);
  };

  const insertLinkIntoParagraph = (idx) => {
    const textarea = paragraphRefs.current[idx];
    const currentText = form.content[idx]?.text || '';
    const result = buildMarkdownLink({ input: textarea, currentText });
    if (!result) return;

    const { nextText, cursorPosition } = result;
    updateBlock(idx, { text: nextText });

    window.requestAnimationFrame(() => {
      const nextTextarea = paragraphRefs.current[idx];
      if (!nextTextarea) return;
      nextTextarea.focus();
      nextTextarea.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const removeBlock = (idx) => {
    setForm((f) => ({
      ...f,
      content: f.content.filter((_, i) => i !== idx)
    }));
  };

  const moveBlock = (idx, dir) => {
    setForm((f) => {
      const next = [...f.content];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, content: next };
    });
  };

  const addListItem = (blockIdx) => {
    setForm((f) => ({
      ...f,
      content: f.content.map((b, i) =>
        i === blockIdx ? { ...b, items: [...(b.items || []), ''] } : b
      )
    }));
  };

  const updateListItem = (blockIdx, itemIdx, value) => {
    setForm((f) => ({
      ...f,
      content: f.content.map((b, i) => {
        if (i !== blockIdx) return b;
        const items = [...(b.items || [])];
        items[itemIdx] = value;
        return { ...b, items };
      })
    }));
  };

  const removeListItem = (blockIdx, itemIdx) => {
    setForm((f) => ({
      ...f,
      content: f.content.map((b, i) => {
        if (i !== blockIdx) return b;
        const items = (b.items || []).filter((_, j) => j !== itemIdx);
        return { ...b, items };
      })
    }));
  };

  // Cover image upload
  const handleCoverUpload = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const path = await uploadImageFile(file);
      setForm((f) => ({ ...f, cover: path }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload cover');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleCoverDrop = (e) => {
    e.preventDefault();
    setCoverDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleCoverUpload(file);
  };

  // Inline image-block upload
  const handleBlockImageUpload = async (blockIdx, file) => {
    if (!file) return;
    setBlockUploading((u) => ({ ...u, [blockIdx]: true }));
    try {
      const path = await uploadImageFile(file);
      updateBlock(blockIdx, { text: path });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setBlockUploading((u) => {
        const copy = { ...u };
        delete copy[blockIdx];
        return copy;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!form.title.trim() || !form.slug.trim() || !form.excerpt.trim() || !form.cover.trim()) {
      toast.error('Title, slug, excerpt and cover are required');
      return;
    }
    // Clean content blocks
    const cleanedContent = form.content
      .map((b) => {
        if (b.type === 'list') {
          const items = (b.items || []).filter((i) => i.trim());
          return items.length ? { type: 'list', items } : null;
        }
        if (b.type === 'image') {
          return b.text && b.text.trim()
            ? {
                type: 'image',
                text: b.text.trim(),
                caption: (b.caption || '').trim() || null
              }
            : null;
        }
        return b.text && b.text.trim() ? { type: b.type, text: b.text.trim() } : null;
      })
      .filter(Boolean);

    if (cleanedContent.length === 0) {
      toast.error('Add at least one content block');
      return;
    }

    const payload = {
      slug: form.slug.trim(),
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      category: form.category,
      author: form.author.trim() || 'Swift Move and Clean Team',
      date: form.date,
      readTime: form.readTime.trim() || '5 min read',
      cover: form.cover.trim(),
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      keywords: form.keywords.trim() || null,
      content: cleanedContent,
      published: form.published
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateBlogPost(token, editingId, payload);
        toast.success('Post updated');
      } else {
        await createBlogPost(token, payload);
        toast.success('Post created');
      }
      closeEditor();
      await loadPosts();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save post';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteBlogPost(token, post.id);
      toast.success('Post deleted');
      await loadPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const togglePublished = async (post) => {
    try {
      await updateBlogPost(token, post.id, { published: !post.published });
      toast.success(post.published ? 'Post unpublished' : 'Post published');
      await loadPosts();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog Posts</h2>
          <p className="text-sm text-gray-500">
            {posts.length} total — {posts.filter((p) => p.published).length} published
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="blog-new-post-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            No posts yet. Click "New Post" to create your first article.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="p-4 flex items-center gap-4 hover:bg-gray-50"
                  data-testid={`admin-blog-row-${post.slug}`}
                >
                  <div
                    className="h-16 w-16 bg-cover bg-center rounded flex-shrink-0"
                    style={{ backgroundImage: `url(${post.cover})` }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                      {post.published ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          Published
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-200 text-gray-700 hover:bg-gray-200">
                          Draft
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      /{post.slug} · {post.category} · {post.date}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => togglePublished(post)}
                      title={post.published ? 'Unpublish' : 'Publish'}
                      data-testid={`blog-toggle-${post.slug}`}
                    >
                      {post.published ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(post)}
                      data-testid={`blog-edit-${post.slug}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(post)}
                      data-testid={`blog-delete-${post.slug}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="blog-editor-modal"
        >
          <div className="bg-white rounded-xl max-w-4xl w-full my-8 shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Post' : 'New Post'}
              </h3>
              <Button size="sm" variant="ghost" onClick={closeEditor} data-testid="blog-editor-close">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Title + Slug */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="blog-title">Title *</Label>
                  <Input
                    id="blog-title"
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setForm((f) => ({
                        ...f,
                        title,
                        // Auto-generate slug only when creating new and slug not manually set
                        slug: !editingId && (!f.slug || f.slug === slugify(f.title))
                          ? slugify(title)
                          : f.slug
                      }));
                    }}
                    data-testid="blog-input-title"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-slug">Slug *</Label>
                  <Input
                    id="blog-slug"
                    value={form.slug}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, slug: slugify(e.target.value) }))
                    }
                    data-testid="blog-input-slug"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label htmlFor="blog-excerpt">Excerpt *</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={insertLinkIntoExcerpt}
                    data-testid="blog-excerpt-insert-link"
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  ref={excerptRef}
                  id="blog-excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  rows={3}
                  data-testid="blog-input-excerpt"
                />
              </div>

              {/* Category + Date + ReadTime */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                  >
                    <SelectTrigger data-testid="blog-select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOG_CATEGORIES.map((c) => (
                        <SelectItem key={c.slug} value={c.slug}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="blog-date">Publication Date *</Label>
                  <Input
                    id="blog-date"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    data-testid="blog-input-date"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-readtime">Read Time</Label>
                  <Input
                    id="blog-readtime"
                    value={form.readTime}
                    onChange={(e) => setForm((f) => ({ ...f, readTime: e.target.value }))}
                    placeholder="5 min read"
                  />
                </div>
              </div>

              {/* Cover + Author */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Cover Image *</Label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setCoverDragging(true);
                    }}
                    onDragLeave={() => setCoverDragging(false)}
                    onDrop={handleCoverDrop}
                    className={`mt-1 border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      coverDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                    data-testid="blog-cover-dropzone"
                  >
                    {form.cover ? (
                      <div className="space-y-2">
                        <img
                          src={resolveImageSrc(form.cover)}
                          alt="Cover preview"
                          className="h-32 w-full object-cover rounded border mx-auto"
                        />
                        <div className="flex justify-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => document.getElementById('blog-cover-file').click()}
                            disabled={coverUploading}
                            data-testid="blog-cover-replace"
                          >
                            {coverUploading ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Upload className="h-3 w-3 mr-1" />
                            )}
                            Replace
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => setForm((f) => ({ ...f, cover: '' }))}
                            data-testid="blog-cover-remove"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-4">
                        <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          Drag & drop an image here, or
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => document.getElementById('blog-cover-file').click()}
                          disabled={coverUploading}
                          data-testid="blog-cover-upload"
                        >
                          {coverUploading ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-3 w-3 mr-1" />
                              Browse Files
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-gray-400 mt-2">PNG, JPG, WEBP up to 10MB</p>
                      </div>
                    )}
                    <input
                      id="blog-cover-file"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleCoverUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <Input
                    className="mt-2 text-xs"
                    value={form.cover}
                    onChange={(e) => setForm((f) => ({ ...f, cover: e.target.value }))}
                    placeholder="Or paste an image URL..."
                    data-testid="blog-input-cover"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-author">Author</Label>
                  <Input
                    id="blog-author"
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                  />
                </div>
              </div>

              {/* Tags + Keywords */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="blog-tags">Tags (comma-separated)</Label>
                  <Input
                    id="blog-tags"
                    value={form.tags}
                    onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                    placeholder="moving tips, packing, indiana"
                  />
                </div>
                <div>
                  <Label htmlFor="blog-keywords">SEO Keywords</Label>
                  <Input
                    id="blog-keywords"
                    value={form.keywords}
                    onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                    placeholder="moving tips Indiana, columbus movers"
                  />
                </div>
              </div>

              {/* Published */}
              <div className="flex items-center gap-2">
                <input
                  id="blog-published"
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  className="h-4 w-4"
                  data-testid="blog-input-published"
                />
                <Label htmlFor="blog-published" className="cursor-pointer">
                  Publish immediately (uncheck to save as draft)
                </Label>
              </div>

              {/* Content Blocks */}
              <div className="border-t pt-5">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Content Blocks</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addBlock('heading')}
                      data-testid="blog-add-heading"
                    >
                      + Heading
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addBlock('paragraph')}
                      data-testid="blog-add-paragraph"
                    >
                      + Paragraph
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addBlock('list')}
                      data-testid="blog-add-list"
                    >
                      + List
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addBlock('image')}
                      data-testid="blog-add-image"
                    >
                      + Image
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {form.content.map((block, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-3 bg-gray-50"
                      data-testid={`blog-block-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase text-gray-500">
                          {block.type}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => moveBlock(idx, -1)}
                            disabled={idx === 0}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => moveBlock(idx, 1)}
                            disabled={idx === form.content.length - 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => removeBlock(idx)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {block.type === 'heading' && (
                        <div className="space-y-2">
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => insertLinkIntoHeading(idx)}
                              data-testid={`blog-heading-insert-link-${idx}`}
                            >
                              <LinkIcon className="h-4 w-4" />
                            </Button>
                          </div>
                          <Input
                            ref={(node) => {
                              if (node) {
                                headingRefs.current[idx] = node;
                              } else {
                                delete headingRefs.current[idx];
                              }
                            }}
                            value={block.text || ''}
                            onChange={(e) => updateBlock(idx, { text: e.target.value })}
                            placeholder="Heading text"
                            className="font-semibold"
                          />
                        </div>
                      )}
                      {block.type === 'paragraph' && (
                        <div className="space-y-2">
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => insertLinkIntoParagraph(idx)}
                              data-testid={`blog-insert-link-${idx}`}
                            >
                              <LinkIcon className="h-4 w-4" />
                            </Button>
                          </div>
                          <Textarea
                            ref={(node) => {
                              if (node) {
                                paragraphRefs.current[idx] = node;
                              } else {
                                delete paragraphRefs.current[idx];
                              }
                            }}
                            value={block.text || ''}
                            onChange={(e) => updateBlock(idx, { text: e.target.value })}
                            rows={4}
                            placeholder="Paragraph text..."
                          />
                        </div>
                      )}
                      {block.type === 'list' && (
                        <div className="space-y-2">
                          {(block.items || []).map((item, iIdx) => (
                            <div key={iIdx} className="flex gap-2">
                              <Input
                                value={item}
                                onChange={(e) => updateListItem(idx, iIdx, e.target.value)}
                                placeholder={`List item ${iIdx + 1}`}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeListItem(idx, iIdx)}
                                className="text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => addListItem(idx)}
                          >
                            + Add item
                          </Button>
                        </div>
                      )}
                      {block.type === 'image' && (
                        <div className="space-y-2" data-testid={`blog-image-block-${idx}`}>
                          {block.text ? (
                            <img
                              src={resolveImageSrc(block.text)}
                              alt={block.caption || 'Inline preview'}
                              className="max-h-48 rounded border bg-white"
                            />
                          ) : (
                            <div className="border-2 border-dashed rounded-lg p-4 text-center bg-white">
                              <ImageIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                              <p className="text-xs text-gray-500">No image selected yet</p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                document.getElementById(`blog-block-image-file-${idx}`).click()
                              }
                              disabled={!!blockUploading[idx]}
                              data-testid={`blog-block-image-upload-${idx}`}
                            >
                              {blockUploading[idx] ? (
                                <>
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="h-3 w-3 mr-1" />
                                  {block.text ? 'Replace' : 'Upload'}
                                </>
                              )}
                            </Button>
                            <input
                              id={`blog-block-image-file-${idx}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleBlockImageUpload(idx, file);
                                e.target.value = '';
                              }}
                            />
                          </div>
                          <Input
                            value={block.text || ''}
                            onChange={(e) => updateBlock(idx, { text: e.target.value })}
                            placeholder="Or paste image URL..."
                            className="text-xs"
                          />
                          <Input
                            value={block.caption || ''}
                            onChange={(e) => updateBlock(idx, { caption: e.target.value })}
                            placeholder="Caption (optional — also used as alt text)"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t pt-5">
                <Button type="button" variant="outline" onClick={closeEditor}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                  data-testid="blog-save-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : editingId ? 'Update Post' : 'Create Post'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogTab;
